import { AgGridReact } from 'ag-grid-react';
import { Theme, themeQuartz } from "ag-grid-community";
import React, { useEffect, useMemo } from 'react';
import { titleCell } from '../../../../components/elements/function';
import Icon from '../../../../components/icons';
import Loader from '../../../../components/loader';
import { useParams } from 'react-router-dom';
import { useDepreciationApi } from '../../../../store/api/hooks/depreciationApiHook';
import moment from 'moment';
import { DATE_FORMAT } from '../../../../_common/constant';
import { useSagesApi } from '../../../../store/api/hooks/sageApiHook';
import Button from '../../../../components/elements/button';
import toast from 'react-hot-toast';
import Currency from '../../../../components/currency';
interface Props {
  _ref: any,
  isDisposed?: boolean;
  hasPermission?: boolean;
}
const AssetRevaluation: React.FC<Props> = ({ _ref, isDisposed, hasPermission = true }) => {
  const myTheme = themeQuartz.withParams({
    headerBackgroundColor: `#F0F0F0`
  });
  const theme = useMemo<Theme | "legacy">(() => {
    return myTheme;
  }, []);

  const params: any = useParams();
  const [rowData, setRowData] = React.useState<any[] | null>([]);
  const depreciationAPI = useDepreciationApi();
  const sageApi: any = useSagesApi();
  const [loadingRowId, setLoadingRowId] = React.useState<string | null>(null);

  const columnDefs = [
    { headerName: 'Effective Date', field: 'effectiveDate', headerClass: 'bg-[#f0f0f0]', width: 120, cellRenderer: (data: any) => data.value ? moment(data.value).format(DATE_FORMAT) : '--' },
    { headerName: 'Asset ID', field: 'assetCode', headerClass: 'bg-[#f0f0f0]', width: 120, cellRenderer: titleCell },
    { headerName: 'Book Value', field: 'actualBookValue', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 120, cellClass: 'ag-right-aligned-cell', cellRenderer: (data: any) => <Currency currency="USD" value={data.value} /> },
    { headerName: 'New Value', field: 'revaluedAmount', headerClass: 'bg-[#f0f0f0] ag-right-aligned-header', width: 120, cellClass: 'ag-right-aligned-cell', cellRenderer: (data: any) => <Currency currency="USD" value={data.value} /> },
    { headerName: 'Old Life Span', field: 'lifeSpan', headerClass: 'bg-[#f0f0f0]', width: 100, cellRenderer: titleCell },
    { headerName: 'New Life Span', field: 'extendedLifeSpan', headerClass: 'bg-[#f0f0f0]', width: 120, cellRenderer: (data: any) => <p>{data.value} </p> },
    { headerName: 'Added Date', field: 'addedDate', headerClass: 'bg-[#f0f0f0]', width: 120, cellRenderer: (data: any) => data.value ? moment(data.value).format(DATE_FORMAT) : '--' },
    { headerName: 'Added By', field: 'addedByName', headerClass: 'bg-[#f0f0f0]', width: 100, cellRenderer: (data: any) => <p>{data.value} </p> },
    { headerName: 'Sage Doc No', field: 'sageDocNo', headerClass: 'bg-[#f0f0f0]', flex: 1, width: 120, cellRenderer: (data: any) => data.value ? data.value : '--' },
    { headerName: 'Sage Posting Date', field: 'sagePostingDate', headerClass: 'bg-[#f0f0f0]', minWidth: 140, flex: 1, cellRenderer: (data: any) => data.value ? moment(data.value).format(DATE_FORMAT) : '--' },
    {
      headerName: "Action",
      field: "sageDocument",
      headerClass: "bg-[#f0f0f0]",
      width: 200,
      cellRenderer: (params: any) => {
        const { sageDocNo, revaluationID, effectiveDate } = params.data;

        const isFutureYear =
          effectiveDate &&
          moment(effectiveDate).year() > moment().year(); // Future year

        const isDisabled =
          loadingRowId === revaluationID || isFutureYear || !hasPermission;

        return (
          <div className="flex items-center justify-between">
            <Button
              onClick={() => postToSage(params.data)}
              disabled={isDisposed || !!sageDocNo || isDisabled}
            >
              {isDisposed
                ? "Already Disposed"
                : sageDocNo
                  ? "Posted"
                  : loadingRowId === revaluationID
                    ? "Posting..."
                    : "Post To Sage"}
            </Button>

          </div>
        );
      },
    }
  ];

  const postToSage = (row: any) => {
    const payload = {
      assetID: row.assetID,
      revaluationID: row.revaluationID,
    };
    setLoadingRowId(row.revaluationID);
    sageApi.PostAssetRevaluationToSage(
      payload,
      (resp: any) => {
        setLoadingRowId(null);
        GetRevaluationList();
        if (resp.successful) {
          toast.success(resp.message || "Posted to Sage successfully");
        } else {
          toast.error(resp.message || "Failed to post to Sage");
        }
      },
      () => {
        setLoadingRowId(null);
        toast.error("Failed to post to Sage");
      }
    );
  };

  const GetRevaluationList = (assetID: any = params.id) => {
    const payload = {
      assetID: assetID,
    };
    depreciationAPI.GetRevaluationList(
      payload,
      (data: any) => {
        setRowData(data.revaluationList);
      },
      (error: any) => { }
    );
  };

  useEffect(() => {
    if (params.id) {
      GetRevaluationList(params.id);
    }
  }, [params]);
  useEffect(() => {
    if (_ref?.current) {
      _ref.current.reload = GetRevaluationList
    }

  }, [_ref]);

  return (
    <div ref={_ref}>
      <div className="flex items-center justify-between h-[60px]">
        <h4 className="font-semibold text-[0.938rem]">Asset Revaluation</h4>
      </div>
      <div className={`mb-3 ag-theme-alpine overflow-auto ${!rowData || rowData.length === 0 || rowData.length <= 2 ? 'h-[150px]' : 'h-[300px]'}`}>
        <AgGridReact
          theme={theme}
          columnDefs={columnDefs}
          rowData={rowData || []}
          pagination={false}
          domLayout="normal"
          ensureDomOrder={true}
          suppressHorizontalScroll={true}
          loadingOverlayComponent={Loader}
          noRowsOverlayComponent={() => (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <div className="text-lg font-medium">No Data Found</div>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default AssetRevaluation;